import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useNotification from 'hooks/useNotification';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Bloklar tartibi va nusxalash.
 *
 * - Tartib: ↑/↓ bilan o'zgartiriladi, "Tartibni saqlash" bosilganda
 *   /admin/block/reorder ga ketma-ketlik yuboriladi (order 1..N).
 * - Nusxalash: alohida oynada NOMI yoziladi. Nom kiritilsa "(Dublikat)"
 *   yoki "(nusxa)" qo'shimchasi qo'shilmaydi — nom aynan yozilganidek bo'ladi.
 *
 * Nusxadagi uylar har doim bo'sh holatda boshlanadi — shartnoma ko'chmaydi.
 */
const ShaxmatkaBlocksManageModal = ({ open, setOpen, blocks = [], refetch }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const sendNotification = useNotification();

  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState('');
  // Nusxalash oynasi: { kind: 'block'|'padezd', id, name, blockId, count }
  const [copy, setCopy] = useState(null);

  useEffect(() => {
    if (open) {
      setItems(
        [...blocks].map((b) => ({
          id: b?.id,
          name: b?.name,
          padezd: b?.padezd,
        })),
      );
      setCopy(null);
    }
  }, [open, blocks]);

  const isDirty = useMemo(
    () =>
      items.map((i) => i.id).join(',') !== blocks.map((b) => b?.id).join(','),
    [items, blocks],
  );

  const move = (index, direction) => {
    const next = index + direction;
    if (next < 0 || next >= items.length) return;
    setItems((prev) => {
      const copyArr = [...prev];
      [copyArr[index], copyArr[next]] = [copyArr[next], copyArr[index]];
      return copyArr;
    });
  };

  const fail = (error) =>
    sendNotification({
      msg: error?.response?.data?.message || error?.message,
      variant: 'error',
    });

  const saveOrder = async () => {
    try {
      setBusy('order');
      await axiosPrivate.post(
        '/admin/block/reorder',
        JSON.stringify({ blocks: items.map((i) => i.id) }),
        { headers: { 'Content-Type': 'application/json' } },
      );
      sendNotification({
        msg: t('shaxmatka.manageBlocks.alerts.orderSaved'),
        variant: 'success',
      });
      refetch?.();
    } catch (error) {
      fail(error);
    } finally {
      setBusy('');
    }
  };

  const submitCopy = async () => {
    if (!copy) return;
    const name = (copy.name || '').trim();
    if (!name) return;

    try {
      setBusy('copy');
      if (copy.kind === 'block') {
        await axiosPrivate.post(
          `/admin/block/dublblock/${copy.id}`,
          JSON.stringify({ name }),
          { headers: { 'Content-Type': 'application/json' } },
        );
        sendNotification({
          msg: t('shaxmatka.manageBlocks.alerts.blockCopied'),
          variant: 'success',
        });
      } else {
        await axiosPrivate.post(
          `/admin/padezd/duplicate/${copy.id}`,
          JSON.stringify({
            name,
            block_id: copy.blockId,
            count: Number(copy.count) || 1,
          }),
          { headers: { 'Content-Type': 'application/json' } },
        );
        sendNotification({
          msg: t('shaxmatka.manageBlocks.alerts.padezdCopied'),
          variant: 'success',
        });
      }
      setCopy(null);
      refetch?.();
    } catch (error) {
      fail(error);
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='md'>
        <DialogTitle className='!text-[16px]'>
          {t('shaxmatka.manageBlocks.title')}
        </DialogTitle>
        <DialogContent>
          <div className='text-[12px] text-muted mb-3'>
            {t('shaxmatka.manageBlocks.hint')}
          </div>

          {items.length === 0 ? (
            <div className='no-data-found-wrapper py-4'>
              <i className='bi bi-exclamation-octagon text-lg mr-1' />
              {t('common.global.noDataFound')}
            </div>
          ) : (
            <div className='flex flex-col gap-2'>
              {items.map((block, index) => (
                <div
                  key={`manage-block-${block.id}`}
                  className='border border-line rounded-lg p-2 bg-white'
                >
                  <div className='flex items-center gap-2'>
                    <span className='w-6 text-center text-[12px] text-muted shrink-0'>
                      {index + 1}
                    </span>
                    <div className='flex flex-col shrink-0'>
                      <IconButton
                        size='small'
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        className='!p-0.5'
                      >
                        <i className='bi bi-chevron-up text-[12px]' />
                      </IconButton>
                      <IconButton
                        size='small'
                        disabled={index === items.length - 1}
                        onClick={() => move(index, 1)}
                        className='!p-0.5'
                      >
                        <i className='bi bi-chevron-down text-[12px]' />
                      </IconButton>
                    </div>
                    <span className='grow font-medium text-[13px] text-ink overflow-hidden text-ellipsis whitespace-nowrap'>
                      {block.name}
                    </span>
                    <Tooltip title={t('shaxmatka.manageBlocks.copyBlockHint')}>
                      <span>
                        <Button
                          size='small'
                          variant='outlined'
                          color='primary'
                          disabled={!!busy}
                          onClick={() =>
                            setCopy({
                              kind: 'block',
                              id: block.id,
                              name: block.name || '',
                              sourceName: block.name,
                            })
                          }
                          startIcon={<i className='bi bi-files text-[12px]' />}
                        >
                          {t('shaxmatka.manageBlocks.copyBlock')}
                        </Button>
                      </span>
                    </Tooltip>
                  </div>

                  {block.padezd?.length > 0 && (
                    <div className='mt-2 pl-8 flex flex-col gap-1.5'>
                      {block.padezd.map((padezd) => (
                        <div
                          key={`manage-padezd-${padezd.id}`}
                          className='flex items-center gap-2 text-[12px]'
                        >
                          <i className='bi bi-door-closed text-muted' />
                          <span className='text-ink w-[140px] overflow-hidden text-ellipsis whitespace-nowrap'>
                            {padezd.name}
                          </span>
                          <span className='text-muted shrink-0'>
                            {padezd.homes?.length || 0}{' '}
                            {t('shaxmatka.manageBlocks.homes')}
                          </span>
                          <Button
                            size='small'
                            variant='outlined'
                            color='primary'
                            disabled={!!busy}
                            onClick={() =>
                              setCopy({
                                kind: 'padezd',
                                id: padezd.id,
                                name: padezd.name || '',
                                sourceName: padezd.name,
                                blockId: block.id,
                                count: 1,
                              })
                            }
                            startIcon={<i className='bi bi-files text-[11px]' />}
                          >
                            {t('shaxmatka.manageBlocks.copy')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className='flex items-center justify-end gap-2 mt-4'>
            <Button
              variant='outlined'
              color='inherit'
              onClick={() => setOpen(false)}
            >
              {t('common.button.close')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              disabled={!isDirty || !!busy}
              onClick={saveOrder}
              startIcon={
                busy === 'order' ? <CircularProgress size={14} /> : undefined
              }
            >
              {t('shaxmatka.manageBlocks.saveOrder')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nusxalash — nomi shu yerda yoziladi */}
      <Dialog
        open={!!copy}
        onClose={() => setCopy(null)}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle className='!text-[15px]'>
          {copy?.kind === 'block'
            ? t('shaxmatka.manageBlocks.copyBlockTitle')
            : t('shaxmatka.manageBlocks.copyPadezdTitle')}
        </DialogTitle>
        <DialogContent>
          <div className='text-[12px] text-muted mb-3'>
            {t('shaxmatka.manageBlocks.source')}: {copy?.sourceName}
          </div>

          <TextField
            autoFocus
            fullWidth
            size='small'
            variant='outlined'
            label={t('shaxmatka.manageBlocks.newName')}
            value={copy?.name || ''}
            onChange={(e) =>
              setCopy((prev) => ({ ...prev, name: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitCopy();
            }}
            className='!mb-3'
          />

          {copy?.kind === 'padezd' && (
            <div className='flex gap-2 mb-1'>
              <div className='grow'>
                <div className='text-[11px] text-muted mb-1'>
                  {t('shaxmatka.manageBlocks.targetBlock')}
                </div>
                <Select
                  size='small'
                  fullWidth
                  value={copy?.blockId || ''}
                  onChange={(e) =>
                    setCopy((prev) => ({ ...prev, blockId: e.target.value }))
                  }
                  className='!text-[13px]'
                >
                  {items.map((b) => (
                    <MenuItem key={`tb-${b.id}`} value={b.id} className='!text-[13px]'>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <div className='w-[110px] shrink-0'>
                <div className='text-[11px] text-muted mb-1'>
                  {t('shaxmatka.manageBlocks.copiesCount')}
                </div>
                <Select
                  size='small'
                  fullWidth
                  value={copy?.count || 1}
                  onChange={(e) =>
                    setCopy((prev) => ({ ...prev, count: e.target.value }))
                  }
                  className='!text-[13px]'
                >
                  {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                    <MenuItem key={`cc-${n}`} value={n} className='!text-[13px]'>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          )}

          {copy?.kind === 'padezd' && Number(copy?.count) > 1 && (
            <div className='text-[11px] text-muted mt-2'>
              {t('shaxmatka.manageBlocks.multiNameHint', {
                first: `${(copy?.name || '').trim()} 1`,
                last: `${(copy?.name || '').trim()} ${copy?.count}`,
              })}
            </div>
          )}

          <div className='text-[11px] text-muted mt-3 flex gap-1.5 items-start'>
            <i className='bi bi-info-circle mt-0.5' />
            <span>{t('shaxmatka.manageBlocks.soldHint')}</span>
          </div>

          <div className='flex items-center justify-end gap-2 mt-4'>
            <Button
              variant='outlined'
              color='inherit'
              onClick={() => setCopy(null)}
              disabled={!!busy}
            >
              {t('common.button.cancel')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              disabled={!(copy?.name || '').trim() || !!busy}
              onClick={submitCopy}
              startIcon={
                busy === 'copy' ? <CircularProgress size={14} /> : undefined
              }
            >
              {t('shaxmatka.manageBlocks.copy')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(ShaxmatkaBlocksManageModal);
